import { FieldMapShape } from './field-map-shape';
import * as L from 'leaflet';
import { Field } from './field';
import { FieldMapContext } from '../field-map-context.model';
import * as turf from '@turf/turf';
import { convertPolygonToTurf } from './shape-utils';
import { Study } from './study';

export class Block extends FieldMapShape {

    id = '';
    isEditing = false;
    isSelected = false;
    name = '';
    polygon: L.Polygon | null = null;
    parentField: Field | null = null;
    editInitialLatLng?: L.LatLng[][];
    type: 'BLOCK' = 'BLOCK';
    turfBoundingBoxPolygon?: turf.helpers.Feature<turf.helpers.Polygon, {}>;
    isOutsideBoundary?: boolean;
    studies: Study[] = [];
    studiesDrawingLayer: L.FeatureGroup;

    constructor(params: {
        map: L.Map,
        id: string,
        name: string,
        polygon: L.Polygon,
        parent: Field,
        fieldMapContext: FieldMapContext
    }) {
        super(params.map);
        this.id = params.id;
        this.name = params.name;
        this.polygon = params.polygon;
        this.parentField = params.parent;
        this.fieldMapContext = params.fieldMapContext;
        this.initialize();
    }

    initialize() {
        this.studiesDrawingLayer = L.featureGroup().addTo(this.map);
        this.fieldMapContext.blocksMap.set(this.id, this);
        this.parentField.blocks.push(this);
        super.initialize();
    }

    draw(): void {
        this.parentField.blocksDrawingLayer.addLayer(this.polygon);
        super.draw();
    }

    delete(): void {

        this.fieldMapContext.beforeDelete(this).then(() => {
            this.reset();

            // Remove the block from the parent field's blocks array
            if (this.parentField) {
                this.fieldMapContext.blocksMap.delete(this.id);
                const index = this.parentField.blocks.indexOf(this);
                if (index > -1) {
                    this.parentField.blocks.splice(index, 1);
                }
            }

            // Remove the block's polygon from the parent field's blocksDrawingLayer
            if (this.parentField && this.parentField.blocksDrawingLayer && this.polygon) {
                this.parentField.blocksDrawingLayer.removeLayer(this.polygon);
            }

            // Delete all studies associated with this block
            this.studies.forEach((study) => {
                study.delete();
            });

            // Clear the studies array
            this.studies = [];

            // Remove the studiesDrawingLayer from the map
            if (this.studiesDrawingLayer) {
                this.studiesDrawingLayer.remove();
            }

            // Remove any editing layers if they exist
            if (this.editInitialLatLng) {
                this.editInitialLatLng = undefined;
            }

            // Remove event listeners
            this.removeListeners();

            // Clear references
            this.parentField = null;
            this.polygon = null;
            this.map = null;
            this.fieldMapContext = null;
            this.studiesDrawingLayer = null;

            // Reset other properties
            this.isEditing = false;
            this.isSelected = false;
            this.isOutsideBoundary = undefined;
            this.turfBoundingBoxPolygon = undefined;
        }, (error) => {
            //
        });
    }

    addListeners() {
        super.addListeners();

        const onShapeDragEnd = (event: L.DragEndEvent, fieldMapShape: FieldMapShape, turfBoundingPolygon?: any): void => {
            if (turfBoundingPolygon) {

                const block = (fieldMapShape as Block);
                const draggedPolygon = event.target as L.Polygon;
                const draggedPolygonTurf = convertPolygonToTurf(event.target as L.Polygon);

                if (!turf.booleanWithin(draggedPolygonTurf, turfBoundingPolygon)) {
                    block.parentField.updateStyle('BOUNDARY_ERROR');
                    block.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    block.parentField.updateStyle('DEFAULT');
                    block.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }

                // Workaround:
                // This is to ensure that the edit/transform handlers are reset correctly
                // when the the draggedPolygon setLangLngs changed.
                if (fieldMapShape.fieldMapContext.editOptionsSelectedValue === 'EDIT') {
                    (draggedPolygon as any).editor.reset();
                    draggedPolygon.enableEdit();
                    draggedPolygon.dragging.enable();
                } else if (fieldMapShape.fieldMapContext.editOptionsSelectedValue === 'ROTATE') {
                    draggedPolygon.transform.enable({ rotation: true, scaling: false });
                    draggedPolygon.transform.reset();
                    draggedPolygon.disableEdit();
                    draggedPolygon.dragging.enable();
                } else if (fieldMapShape.fieldMapContext.editOptionsSelectedValue === 'RESIZE') {
                    draggedPolygon.transform.enable({ rotation: false, scaling: true });
                    draggedPolygon.transform.reset();
                    draggedPolygon.disableEdit();
                    draggedPolygon.dragging.enable();
                }
            }

        };

        const onFieldMapShapeTransformEnd = (event: L.LeafletEvent, fieldMapShape: FieldMapShape, turfBoundingPolygon?: any): void => {
            if (turfBoundingPolygon) {
                const block = (fieldMapShape as Block);
                const rotatedPolygon = event.target as L.Polygon;
                const rotatedPolygonTurf = convertPolygonToTurf(event.target as L.Polygon);

                if (!turf.booleanWithin(rotatedPolygonTurf, turfBoundingPolygon)) {
                    // If the Block is rotated or resized and extends beyond its parent Field boundaries,
                    // a boundary error should be displayed.
                    block.parentField.updateStyle('BOUNDARY_ERROR');
                    block.isOutsideBoundary = true;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                } else {
                    block.parentField.updateStyle('DEFAULT');
                    block.isOutsideBoundary = false;
                    this.fieldMapContext.shapeTransforming({ isOutOfBoundary: this.isOutsideBoundary }, this);
                }

                if (block.fieldMapContext.editOptionsSelectedValue === 'ROTATE') {
                    rotatedPolygon.transform.enable({ rotation: true, scaling: false });
                    rotatedPolygon.transform.reset();
                    rotatedPolygon.dragging.enable();
                } else if (block.fieldMapContext.editOptionsSelectedValue === 'RESIZE') {
                    rotatedPolygon.transform.enable({ rotation: false, scaling: true });
                    rotatedPolygon.transform.reset();
                    rotatedPolygon.dragging.enable();
                }
            }
        };

        this.polygon.on('dragstart', (e) => {
            // Remember the original geometry/location of the Block field map shape
            this.editInitialLatLng = this.polygon.getLatLngs() as L.LatLng[][];
            // Contain the Block polygon inside its parent Field. When the Block is dragged outside its parent Field, it should
            // revert back to its original location, even when only a part of the Block is outside its parent Field.
            this.turfBoundingBoxPolygon = convertPolygonToTurf(this.parentField.polygon);
            this.polygon.once('dragend', (evt) => {
                onShapeDragEnd(evt, this, this.turfBoundingBoxPolygon);
            });
        });

        this.polygon.on('transformstart', (event) => {
            // Remember the original geometry/location of the Block field map shape
            this.editInitialLatLng = this.polygon.getLatLngs() as L.LatLng[][];
            // Ensure the Block polygon remains within its parent Field. If the Block is rotated or resized
            // and extends beyond its parent Field boundaries, a boundary error should be displayed.
            this.turfBoundingBoxPolygon = convertPolygonToTurf(this.parentField.polygon);
            this.polygon.once('transformed', (e) => {
                onFieldMapShapeTransformEnd(e, this, this.turfBoundingBoxPolygon);
            });
        });
    }

    isEditable(): boolean {
        return this.studies.length === 0 && !this.isOutsideBoundary;
    }

    isResizable(): boolean {
        return this.studies.length === 0 && !this.isOutsideBoundary;
    }

    isRotatable(): boolean {
        return this.studies.length === 0 && !this.isOutsideBoundary;
    }

    isSaveable(): boolean {
        return this.isEditing && this.studies.length === 0 && !this.isOutsideBoundary;
    }

    isDeletable(): boolean {
        return this.studies.length === 0 && !this.isOutsideBoundary;
    }
}
